import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { OrderResponse } from "../types/order";
import { parseMetadataNumber } from "@/lib/utils/numbers";

// Base selector
const selectOrderState = (state: RootState) => state.order;

// Basic state selectors
export const selectInitComplete = (state: RootState) =>
  selectOrderState(state).initializeComplete;
export const selectSubmitLoading = (state: RootState) =>
  selectOrderState(state).submitLoading;

// Fetched order selectors
export const selectFetchedOrderState = createSelector(
  [selectOrderState],
  (orderState) => orderState.fetchedOrder
);

export const selectFetchedOrder = createSelector(
  [selectFetchedOrderState],
  (fetchedOrder): OrderResponse | null => fetchedOrder.data
);

export const selectFetchedOrderStatus = createSelector(
  [selectFetchedOrderState],
  (fetchedOrder) => ({
    loading: fetchedOrder.loading,
    error: fetchedOrder.error,
    isLoaded: !fetchedOrder.loading && !!fetchedOrder.data,
  })
);

// Create order selectors
export const selectCreateOrderState = createSelector(
  [selectOrderState],
  (orderState) => orderState.createOrder
);

export const selectCreateOrderStatus = createSelector(
  [selectCreateOrderState],
  (createOrder) => ({
    loading: createOrder.loading,
    error: createOrder.error,
    isPending: createOrder.loading && !createOrder.error,
  })
);

// Cart selectors
export const selectCartStatus = createSelector(
  [selectOrderState],
  (orderState) => ({
    loading: orderState.addToCartLoading,
    error: orderState.addToCartError,
  })
);

// Update order selectors
export const selectUpdateOrderState = createSelector(
  [selectOrderState],
  (orderState) => orderState.updateOrder
);

export const selectUpdateOrderStatus = createSelector(
  [selectUpdateOrderState],
  (updateOrder) => ({
    loading: updateOrder.loading === "pending",
    error: updateOrder.error,
    isComplete: updateOrder.loading === "fulfilled",
  })
);

// Combined/derived selectors
export const selectOrderStatus = createSelector(
  [
    selectInitComplete,
    selectFetchedOrderStatus,
    selectCreateOrderStatus,
    selectSubmitLoading,
  ],
  (isInitialized, fetchedStatus, createStatus, isSubmitting) => ({
    isInitialized,
    isLoading: fetchedStatus.loading || createStatus.loading || isSubmitting,
    hasOrder: fetchedStatus.isLoaded,
    isSubmitting,
    hasErrors: !!(fetchedStatus.error || createStatus.error),
  })
);

// Add a type guard function
export const selectOrderPromotion = createSelector(
  [selectFetchedOrder],
  (
    order
  ): {
    hasPromotion: boolean;
    promotionalPrice?: number;
    regularPrice?: number;
    validityPeriod?: string;
  } => {
    const promotionData = order?.metadata?.promotion;

    if (!promotionData) {
      return { hasPromotion: false };
    }

    // Parse the promotion string if needed
    const promotion =
      typeof promotionData === "string"
        ? JSON.parse(promotionData)
        : promotionData;

    const regularPrice = parseMetadataNumber(
      order.metadata?.["Flex Monthly Price"],
      0
    );

    return {
      hasPromotion: true,
      promotionalPrice: promotion.appliedPrice,
      regularPrice,
      validityPeriod: promotion.validityPeriod,
    };
  }
);

export const selectOrderPromotionVoucher = createSelector(
  [selectFetchedOrder],
  (order): string | undefined => {
    const promotionData = order?.metadata?.promotion;
    if (!promotionData) return undefined;

    const promotion =
      typeof promotionData === "string"
        ? JSON.parse(promotionData)
        : promotionData;

    return promotion.circulyVoucherCode;
  }
);

// Steps selectors
export const selectOrderSteps = createSelector(
  [selectOrderState],
  (orderState) => orderState.steps
);

export const selectCurrentStep = createSelector(
  [selectOrderSteps],
  (steps) => steps.currentStep
);

export const selectStepStates = createSelector(
  [selectOrderSteps],
  (steps) => steps.stepStates
);

export const selectStepProgress = createSelector(
  [selectStepStates],
  (stepStates) => ({
    bankIdCompleted: stepStates.BANK_ID.completed,
    deliveryCompleted: stepStates.DELIVERY.completed,
    paymentCompleted: stepStates.PAYMENT.completed,
    allStepsCompleted: Object.values(stepStates).every(
      (step) => step.completed
    ),
  })
);
